import * as React from 'react';
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldLabel,
  Switch
} from '@bliks-io/ui';

export interface FormFieldSwitchProps extends Omit<
  React.ComponentPropsWithoutRef<typeof Switch>,
  'onCheckedChange' | 'checked'
> {
  /**
   * Field label
   */
  label?: string;

  /**
   * Field description/helper text
   */
  description?: string;

  /**
   * Error message
   */
  error?: string;

  /**
   * Checked state
   */
  checked?: boolean;

  /**
   * Change handler
   */
  onCheckedChange?: (checked: boolean) => void;

  /**
   * Field ID
   */
  id?: string;

  /**
   * Field name
   */
  name?: string;

  /**
   * Field orientation (horizontal is recommended for switches)
   */
  orientation?: 'vertical' | 'horizontal' | 'responsive';

  /**
   * Additional class for Field wrapper
   */
  fieldClassName?: string;
}

/**
 * Reusable form field component combining Field + Switch
 * Can be used standalone or wrapped by RHF adapter components
 * Uses horizontal orientation by default for better switch layout
 */
export const FormFieldSwitch = React.forwardRef<
  React.ElementRef<typeof Switch>,
  FormFieldSwitchProps
>(
  (
    {
      label,
      description,
      error,
      checked,
      onCheckedChange,
      id,
      name,
      orientation = 'horizontal',
      fieldClassName,
      ...switchProps
    },
    ref
  ) => {
    const fieldId = id || name;
    const hasError = !!error;

    return (
      <Field orientation={orientation} className={fieldClassName} data-invalid={hasError}>
        <Switch
          ref={ref}
          id={fieldId}
          checked={checked}
          onCheckedChange={onCheckedChange}
          aria-invalid={hasError}
          {...switchProps}
        />
        <FieldContent>
          {label && <FieldLabel htmlFor={fieldId}>{label}</FieldLabel>}
          {description && <FieldDescription>{description}</FieldDescription>}
          {error && <FieldError>{error}</FieldError>}
        </FieldContent>
      </Field>
    );
  }
);

FormFieldSwitch.displayName = 'FormFieldSwitch';
